import { CheckCircle, Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFetcher, useNavigate, useSearchParams } from "react-router-dom";
import { api, wrappedCollectionFetcher } from "services/api.service";
import useSWR from "swr";
import { BulkOrder } from "types/bulkOrder";
import { Inventory } from "types/inventory";

const MANIFEST_SEARCH_MIN_LENGTH = 5;

const formFieldProps: TextFieldProps = {
  fullWidth: true,
  required: true,
  variant: "outlined",
  size: "small",
};

const optionalFormFieldProps: TextFieldProps = {
  fullWidth: true,
  variant: "outlined",
  size: "small",
};

export function BulkOrderDialog() {
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [wasSubmitting, setWasSubmitting] = useState(false);
  const [bulkOrderExists, setBulkOrderExists] = useState<boolean>(false);
  const [customerNameValue, setCustomerNameValue] = useState("");
  const [unitCount, setUnitCount] = useState(0);
  const [liveAssociatedCount, setLiveAssociatedCount] = useState(0);
  const [isPullingManifest, setIsPullingManifest] = useState(false);
  const handleCustomerNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCustomerNameValue(event.target.value);

  const [manifestId, setManifestId] = useState("");
  const [manifestSelected, setManifestSelected] = useState(false);
  const [manifestSearchQuery, setManifestSearchQuery] = useState("");

  const [warehouseValue, setWarehouseValue] = useState("");
  const handleWarehouseChange = (event: React.ChangeEvent<HTMLInputElement>) => setWarehouseValue(event.target.value);

  const [dueDateValue, setDueDateValue] = useState("");
  const handleDueDateChange = (event: React.ChangeEvent<HTMLInputElement>) => setDueDateValue(event.target.value);

  const createAction =
    searchParams.toString() === "" ? "/bulk-orders/new" : `/bulk-orders/new?${searchParams.toString()}`;
  const isSubmitting = fetcher.state === "submitting";

  const handleDismiss = useCallback(() => navigate(-1), [navigate]);

  useEffect(() => {
    if (fetcher.state === "submitting") {
      setWasSubmitting(true);
    }
  }, [fetcher.state]);

  useEffect(() => {
    if (wasSubmitting && fetcher.state === "idle" && fetcher.data === null) {
      setWasSubmitting(false);
      handleDismiss();
    }
  }, [wasSubmitting, handleDismiss, fetcher.state, fetcher.data]);

  const debouncedSetManifestSearchQuery = useMemo(
    () =>
      debounce((value: string) => {
        setManifestSearchQuery(value);
      }, 300),
    [],
  );

  const manifestSearchKey = useMemo(() => {
    if (manifestSearchQuery.length < MANIFEST_SEARCH_MIN_LENGTH) return null;
    return `/inventories?filter[]=manifest_id,contains,${encodeURIComponent(manifestSearchQuery)}&filter[]=bulk_order_id,is_empty&page[size]=25`;
  }, [manifestSearchQuery]);

  const { data: manifestSearchResults, isLoading: isSearchingManifests } = useSWR(
    manifestSearchKey,
    wrappedCollectionFetcher<Inventory>,
  );

  const manifestOptions = useMemo(() => {
    if (!manifestSearchResults?.rows) return [];
    return Array.from(
      new Set(
        manifestSearchResults.rows.map((inventory) => inventory.manifestId).filter((id): id is string => Boolean(id)),
      ),
    );
  }, [manifestSearchResults]);

  const clearManifestDependentFields = useCallback(() => {
    setManifestSelected(false);
    setManifestId("");
    setUnitCount(0);
    setLiveAssociatedCount(0);
    setIsPullingManifest(false);
    setCustomerNameValue("");
    setWarehouseValue("");
    setDueDateValue("");
    setBulkOrderExists(false);
  }, []);

  const populateFromManifestId = useCallback(async (selectedManifestId: string) => {
    setManifestId(selectedManifestId);
    setManifestSelected(true);
    setIsPullingManifest(true);

    const manifestIdParts = selectedManifestId.split("-");
    const dueDatePart = manifestIdParts[1];
    const warehouseCode = manifestIdParts[2];
    const customerName = manifestIdParts[3];

    if (customerName) setCustomerNameValue(customerName);
    if (warehouseCode === "1A") setWarehouseValue("1");
    else if (warehouseCode === "2A") setWarehouseValue("2");
    else setWarehouseValue("");

    if (dueDatePart) {
      const parsedDueDate = new Date(
        `${dueDatePart.slice(0, 2)}-${dueDatePart.slice(2, 4)}-20${dueDatePart.slice(4, 6)}`,
      );
      if (!isNaN(parsedDueDate.getTime())) {
        setDueDateValue(parsedDueDate.toISOString().split("T")[0]);
      }
    } else {
      setDueDateValue("");
    }

    let pulledCount = 0;
    try {
      const inventoryResults = await wrappedCollectionFetcher<Inventory>(
        `/inventories?filter[]=manifest_id,is,${encodeURIComponent(selectedManifestId)}&filter[]=bulk_order_id,is_empty&filter[]=state,not_in,invoiced&page[size]=1000`,
      );
      pulledCount = inventoryResults.meta?.count ?? inventoryResults.rows.length;
      setLiveAssociatedCount(pulledCount);
    } catch {
      setLiveAssociatedCount(0);
    }

    try {
      const { data } = await api.get<BulkOrder>(`/bulk_orders/${encodeURIComponent(selectedManifestId)}`);
      setBulkOrderExists(true);
      setUnitCount(data.unitCount);
    } catch {
      setBulkOrderExists(false);
      setUnitCount(pulledCount);
    } finally {
      setIsPullingManifest(false);
    }
  }, []);

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>
        {bulkOrderExists ? "Edit" : "New"} {searchParams.get("type") === "WholesaleOrder" ? "Wholesale" : "Donation"}{" "}
        Order
      </DialogTitle>
      <DialogContent>
        <fetcher.Form
          action={bulkOrderExists ? `/bulk-orders/${manifestId}/update` : createAction}
          id="bulk-order-form"
          method={bulkOrderExists ? "put" : "post"}
        >
          <Stepper orientation="vertical">
            <Step active={true}>
              <StepLabel>Manifest Details</StepLabel>
              <StepContent>
                <Grid container columns={4} spacing={2}>
                  <Grid size={4}>
                    <input type="hidden" name="manifestId" value={manifestId} />
                    <Autocomplete
                      freeSolo
                      options={manifestOptions}
                      value={manifestId}
                      loading={isSearchingManifests}
                      filterOptions={(options) => options}
                      onChange={(_event, newValue) => {
                        if (typeof newValue === "string" && newValue) {
                          void populateFromManifestId(newValue);
                        } else {
                          clearManifestDependentFields();
                        }
                      }}
                      onInputChange={(_event, newInputValue, reason) => {
                        if (reason === "input") {
                          debouncedSetManifestSearchQuery(newInputValue);
                          if (!newInputValue) {
                            clearManifestDependentFields();
                            return;
                          }
                          setManifestId(newInputValue);
                          setManifestSelected(false);
                        } else if (reason === "clear") {
                          clearManifestDependentFields();
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Manifest ID"
                          fullWidth
                          variant="outlined"
                          size="small"
                          required
                          placeholder={`Type at least ${MANIFEST_SEARCH_MIN_LENGTH} characters to search`}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isSearchingManifests ? <CircularProgress color="inherit" size={16} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            },
                          }}
                        />
                      )}
                    />
                    {isPullingManifest && (
                      <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Pulling inventory for this manifest...
                        </Typography>
                      </Box>
                    )}
                    {manifestSelected && !isPullingManifest && (
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          bgcolor: "background.paper",
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                          <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />
                          <Typography variant="body2">
                            Pulled <strong>{liveAssociatedCount} inventories</strong> for this manifest.
                          </Typography>
                        </Stack>
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                          <Grid size={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", letterSpacing: 0.4, display: "block" }}
                            >
                              Unit Count (Original)
                            </Typography>
                            <Typography variant="h5" fontWeight={500}>
                              {unitCount}
                            </Typography>
                          </Grid>
                          <Grid size={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", letterSpacing: 0.4, display: "block" }}
                            >
                              Live Associated Count
                            </Typography>
                            <Typography variant="h5" fontWeight={500} color="success.main">
                              {liveAssociatedCount}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Typography variant="caption" color="text.secondary">
                          Original count locks at creation; live count re-checks on a cadence to catch anything that
                          sells on ecom before pickup.
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid size={2}>
                    <TextField
                      name="customerName"
                      value={customerNameValue}
                      onChange={handleCustomerNameChange}
                      label="Customer Name"
                      sx={{ textTransform: "capitalize" }}
                      {...formFieldProps}
                    />
                  </Grid>
                  <Grid size={2}>
                    <input type="hidden" name="unitCount" value={unitCount} />
                    <TextField
                      select
                      name="warehouseId"
                      value={warehouseValue}
                      onChange={handleWarehouseChange}
                      label="Warehouse"
                      defaultValue=""
                      {...formFieldProps}
                    >
                      <MenuItem value="1">New York</MenuItem>
                      <MenuItem value="2">San Leandro</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={2}>
                    <TextField select name="shippingMethod" label="Shipping Method" defaultValue="" {...formFieldProps}>
                      <MenuItem value="ground">Ground</MenuItem>
                      <MenuItem value="freight">Freight</MenuItem>
                      <MenuItem value="local_pickup">Local Pickup</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={2}>
                    <TextField
                      name="dueDate"
                      type="date"
                      value={dueDateValue}
                      onChange={handleDueDateChange}
                      label="Due Date"
                      InputLabelProps={{ shrink: true }}
                      {...formFieldProps}
                    />
                  </Grid>
                </Grid>
              </StepContent>
            </Step>
            <Step active={manifestSelected}>
              <StepLabel>Payment Details (optional)</StepLabel>
              {manifestSelected && (
                <StepContent>
                  <Grid container columns={3} spacing={2}>
                    <Grid size={2}>
                      <TextField name="invoiceId" label="Invoice ID" defaultValue="" {...optionalFormFieldProps} />
                    </Grid>
                    <Grid size={1}>
                      <TextField
                        name="paymentDate"
                        type="date"
                        label="Payment Date"
                        defaultValue=""
                        InputLabelProps={{ shrink: true }}
                        {...optionalFormFieldProps}
                      />
                    </Grid>
                    <Grid size={1}>
                      <Autocomplete
                        disablePortal
                        freeSolo
                        options={["Bank of America"]}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="depositTo"
                            label="Deposit To (Bank Name/Account)"
                            placeholder="Select or enter your own"
                            {...optionalFormFieldProps}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={1}>
                      <TextField
                        name="soldPrice"
                        label="Sold Price"
                        type="number"
                        defaultValue=""
                        placeholder="100.20"
                        slotProps={{
                          input: {
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputMode: "decimal",
                            inputProps: {
                              step: 0.01,
                              min: 0,
                            },
                          },
                        }}
                        {...optionalFormFieldProps}
                      />
                    </Grid>
                    <Grid size={1}>
                      <TextField
                        select
                        name="paymentMethod"
                        label="Payment Method"
                        defaultValue=""
                        {...optionalFormFieldProps}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="ach">ACH</MenuItem>
                        <MenuItem value="credit">Credit Card</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </StepContent>
              )}
            </Step>
          </Stepper>
        </fetcher.Form>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", gap: 2, mb: 2 }}>
        <Button type="submit" form="bulk-order-form" variant="contained" disabled={isSubmitting || !manifestSelected}>
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
      <IconButton onClick={handleDismiss} sx={{ position: "absolute", top: "4px", right: "4px" }}>
        <Close />
      </IconButton>
    </Dialog>
  );
}
